/** Pure helpers for scene comment threads (kept out of the component file so
 * fast refresh keeps working there). */
import { StoryComment } from "../../types/storyMap";

export interface Thread {
  opener: StoryComment;
  replies: StoryComment[];
}

/** "just now", "5 min ago", "3 days ago", else a short date. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: days > 300 ? "numeric" : undefined,
  });
}

/** Group a story's comments into this scene's threads, oldest first. */
export function threadsForScene(comments: StoryComment[], sceneId: string): Thread[] {
  const openers = comments.filter((c) => c.scene_id === sceneId && !c.parent_id);
  const repliesByParent = new Map<string, StoryComment[]>();
  comments.forEach((c) => {
    if (!c.parent_id) return;
    const list = repliesByParent.get(c.parent_id) ?? [];
    list.push(c);
    repliesByParent.set(c.parent_id, list);
  });
  return openers.map((opener) => ({
    opener,
    replies: repliesByParent.get(opener.id) ?? [],
  }));
}

/** Open (unresolved) thread count for a scene — the badge number. */
export function openThreadCount(comments: StoryComment[], sceneId: string): number {
  return comments.filter((c) => c.scene_id === sceneId && !c.parent_id && !c.resolved).length;
}
